import { axios } from '@/plugins/http-axios';
import { defineStore } from 'pinia';
import { IBaseOffice } from './bank';
import { PRIVILEGES } from '@/views/bank/constants';
import { useMainStore } from './mainStore';
import { useSystemStore } from './system';
import { ref, computed } from 'vue';

function sortMenu(menu: IMenu[]): (IMenu | undefined)[] {
  const lku = menu.find((item) => item.url === '/admin');
  const lkb = menu.find((item) => item.url === '/bank');
  const lko = menu.find((item) => item.url === '/report');
  return [lku, lkb, lko].filter((item) => !!item);
}

export interface ISimpleUser {
  id: string;
  fio: string;
  name: string;
  email: string;
}

export interface IUser {
  id: string;
  username: string;
  fio: string;
  firstName: string;
  lastName: string;
  phone: string;
  parName: string;
  email: string;
  domainName: string;
  firstPassword: boolean;
  schemaDesign: string;
  roles: IRole[];
  employee: IEmployee;
  filterSettings: {
    radar?: IFilterSettings;
  };
  offices: IOfficeUser[];
  activeRole: IRole;
  signContract: boolean;
  dateDocumentStart: string;
  dateDocumentEnd: string;
  userPhotoId: null | string;
}

export interface IFilterSettings {
  limit: number;
  offset: number;
  substring: string;
  updateDateFrom: string;
  updateDateTo: string;
  datefrom: string;
  agents: string[];
  dateto: string;
  closedatefrom: string;
  closedateto: string;
  annulPeriod: number | string;
  signers: string[];
  takers: string[];
  offices: number[];
  statusid: number[];
  applicationRequestTypes?: string[] | null;
  isAgentApp?: boolean | null;
  sorted: {
    field: string;
    orderBy: 'ASC' | 'DESC' | '';
  };
}

export interface IFilterSettingsForm {
  limit: number;
  offset: number;
  substring: string;
  sorted: {
    field: string;
    orderBy: 'ASC' | 'DESC' | '';
  };
  appId: string;
}

interface IEmployee {
  id: string;
  userid: string;
  surname?: string;
  name: string;
  parname: string;
  dateDocument?: string;
  numberDocument: string;
  nameEmployee: string;
  email: string;
  deleted: boolean;
  revokeFromApplication: boolean;
  fio: string;
}

export interface IOfficeUser extends IBaseOffice {
  regionId: number;
  sortedItem: number;
}

export interface IMenu {
  id: number;
  url: string;
  name: string;
  parentUrl?: string;
  children: IMenu[];
  text: string;
  href: string;
  path: string;
}

export interface IAppBlock {
  id: number;
  orderNum: number;
  code: string;
  name: string;
  active: boolean;
}

export interface IPrivileges {
  id: number;
  name: string;
  description: string;
  isCreated: boolean;
  isRead: boolean;
  isUpdate: boolean;
  isDelete: boolean;
  isExecute: boolean;
}

export interface IRole {
  id: number;
  name: string;
  active: boolean;
  menuTree: IMenu[];
  menuFlat: IMenu[];
  applicationBlocks: IAppBlock[];
  privileges: IPrivileges[];
  defaultPage?: number | null;
}

interface ISelectRole {
  text: string;
  value: IRole;
}

const defaultUser: IUser = {
  id: '',
  username: '',
  fio: '',
  firstName: '',
  lastName: '',
  parName: '',
  domainName: '',
  phone: '',
  employee: {
    id: '',
    userid: '',
    name: '',
    parname: '',
    numberDocument: '',
    nameEmployee: '',
    email: '',
    deleted: false,
    revokeFromApplication: false,
    fio: '',
  },
  email: '',
  firstPassword: false,
  roles: [],
  offices: [],
  activeRole: {
    id: 0,
    name: '',
    active: false,
    menuTree: [],
    menuFlat: [],
    applicationBlocks: [],
    privileges: [],
  },
  filterSettings: {},
  schemaDesign: '',
  signContract: false,
  dateDocumentStart: '',
  dateDocumentEnd: '',
  userPhotoId: '',
};

const templateFilter: IFilterSettings = {
  takers: [],
  offices: [],
  statusid: [],
  agents: [],
  signers: [],
  updateDateFrom: '',
  updateDateTo: '',
  closedatefrom: '',
  closedateto: '',
  datefrom: '',
  dateto: '',
  annulPeriod: '',
  offset: 0,
  limit: 10,
  substring: '',
  sorted: {
    field: '',
    orderBy: '',
  },
  isAgentApp: null,
  applicationRequestTypes: [],
};

const templateFilterForm: IFilterSettingsForm = {
  offset: 0,
  limit: 10,
  substring: '',
  sorted: {
    field: '',
    orderBy: '',
  },
  appId: '',
};

export const useUserStore = defineStore('user', () => {
  // State
  const user = ref<IUser>(structuredClone(defaultUser));
  const filterForm = ref<IFilterSettingsForm>(structuredClone(templateFilterForm));
  const authorized = ref<'authorized' | 'no_authorized' | undefined>(undefined);
  const phonegroup = ref<any>({});
  const agent = ref<any>([]);

  // Getters
  const simpleUser = computed<ISimpleUser>(() => ({
    id: user.value.id,
    name: user.value.username,
    email: user.value.email,
    fio: user.value.fio,
  }));

  const isFirstPassword = computed(() => user.value.firstPassword);

  const filter = computed<IFilterSettings>(() => {
    if (user.value.filterSettings && user.value.filterSettings.radar) {
      return user.value.filterSettings.radar;
    } else {
      return structuredClone(templateFilter);
    }
  });

  const getAgentBlock = computed(() => agent.value);
  const role = computed<IRole>(() => user.value.activeRole);

  const listUserRoles = computed<ISelectRole[]>(() =>
    user.value.roles.map((item) => ({ text: item.name, value: item })),
  );

  const privileges = computed(() => user.value.activeRole.privileges);

  const isPrivileges = computed(
    () =>
      (name: string): boolean =>
        user.value.activeRole.privileges.some((priv: IPrivileges) => priv.name === name),
  );

  const hasPrivilegeGenerationOfSubscriptionForms = computed(() =>
    isPrivileges.value(PRIVILEGES.GENERATION_OF_SUBSCRIPTION_FORMS),
  );

  const hasPrivilegeRequestDigitalProfileFl = computed(() => isPrivileges.value(PRIVILEGES.REQUEST_DIGITAL_PROFILE_FL));

  const hasSendingActToEdo = computed(() => isPrivileges.value(PRIVILEGES.SENDING_ACT_TO_EDO));

  const hasSendingDocumentsToEdo = computed(() => isPrivileges.value(PRIVILEGES.SENDING_DOCUMENTS_TO_EDO));

  const hasRenamingApplicationFiles = computed(() => isPrivileges.value(PRIVILEGES.RENAMING_APPLICATION_FILES));

  const hasEditingDocumentType = computed(() => isPrivileges.value(PRIVILEGES.EDITING_A_DOCUMENT_TYPE));
  const hasOrigProvided = computed(() => isPrivileges.value(PRIVILEGES.ORIG_PROVIDED));
  const hasReRequestDocument = computed(() => isPrivileges.value(PRIVILEGES.REQUEST_FOR_ADD_DOCS));
  const hasAbsClientStatus = computed(() => isPrivileges.value(PRIVILEGES.ABS_CLIENT_STATUS));
  const hasSettingProcessingTime = computed(() => isPrivileges.value(PRIVILEGES.SETTING_PROCESSING_TIME));

  const blocks = computed(() => user.value.activeRole.applicationBlocks);
  const menu = computed<IMenu[]>(() => user.value.activeRole.menuTree);
  const menuFlat = computed<IMenu[]>(() => user.value.activeRole.menuFlat);

  const checkRoute = computed(
    () =>
      (route: string): boolean =>
        menuFlat.value.some((item: IMenu) => item.url === route),
  );

  const defaultPage = computed(() => {
    const dPageId = user.value.activeRole.defaultPage;
    if (dPageId === null || dPageId === undefined) {
      return null;
    }
    return menuFlat.value.find((v) => v.id === dPageId);
  });

  const offices = computed(() => user.value.offices);
  const getPhoneGroup = computed(() => phonegroup.value);

  function setAgent(value: any) {
    agent.value = value;
  }

  function setAuthorized(value: 'authorized' | 'no_authorized' | undefined) {
    authorized.value = value;
  }

  function setUser(data: IUser = defaultUser) {
    if (data.activeRole && data.activeRole.menuTree) {
      // @ts-ignore
      data.activeRole.menuTree = sortMenu(data.activeRole.menuTree);
    }
    for (const key in defaultUser) {
      (user.value as any)[key] = (data as any)[key] || (defaultUser as any)[key];
    }
  }

  function setFilter(filter: any) {
    if (!('radar' in user.value.filterSettings)) {
      user.value.filterSettings = Object.assign({}, user.value.filterSettings, { radar: {} });
    }
    if (!filter) {
      if (user.value.filterSettings.radar) {
        user.value.filterSettings.radar = structuredClone(templateFilter);
      }
    } else if (user.value.filterSettings.radar) {
      for (const key in templateFilter) {
        if (key in user.value.filterSettings.radar) {
          if (key in filter) {
            (user.value.filterSettings.radar as any)[key] = filter[key];
            if (user.value.filterSettings.radar && !user.value.filterSettings.radar.agents) {
              user.value.filterSettings.radar.agents = [];
            }
          }
        } else {
          (user.value.filterSettings.radar as any)[key] = filter[key] || (templateFilter as any)[key];
        }
      }
    }
  }

  function updatePhoneGroup(payload: any) {
    phonegroup.value = payload;
  }

  function resetGroup() {
    phonegroup.value = {};
  }

  async function getUser(): Promise<string> {
    const mainStore = useMainStore();
    const systemStore = useSystemStore();

    try {
      const resp = await axios.get<IUser>(mainStore.getUri.userInfo);
      setAuthorized('authorized');
      setUser(resp.data);
      if (!role.value || !role.value.id) {
        systemStore.setAccessRole(false);
      }
      return 'authorized';
    } catch (e: any) {
      console.log('USER:', e.response);
      if ('response' in e && e.response instanceof Object) {
        if (e.response.status === 401) {
          setAuthorized('no_authorized');
          return Promise.resolve('no_authorized');
        }
        if (e.response.status === 423) {
          if (e.response.data.errorMessage === 'Role block ') {
            setAuthorized('authorized');
            return Promise.resolve('authorized');
          } else {
            setAuthorized('blocked' as any);
            return Promise.resolve('blocked');
          }
        }
      }

      return Promise.reject(e);
    }
  }

  async function setActiveRole(role: IRole) {
    const mainStore = useMainStore();
    // @ts-ignore
    role.menuTree = sortMenu(role.menuTree);
    user.value.activeRole = role;
    return axios.post(mainStore.getUri.changeRole, { id: role.id });
  }

  // Return everything
  return {
    // State
    user,
    authorized,
    phonegroup,
    agent,
    // Getters
    simpleUser,
    isFirstPassword,
    filterForm,
    filter,
    getAgentBlock,
    role,
    listUserRoles,
    privileges,
    isPrivileges,
    hasPrivilegeGenerationOfSubscriptionForms,
    hasPrivilegeRequestDigitalProfileFl,
    hasSendingActToEdo,
    hasSendingDocumentsToEdo,
    hasRenamingApplicationFiles,
    hasEditingDocumentType,
    hasOrigProvided,
    hasReRequestDocument,
    hasAbsClientStatus,
    hasSettingProcessingTime,
    blocks,
    menu,
    menuFlat,
    checkRoute,
    offices,
    getPhoneGroup,
    // Actions
    setActiveRole,
    setAgent,
    setAuthorized,
    setUser,
    setFilter,
    updatePhoneGroup,
    resetGroup,
    getUser,
    defaultPage
  };
});

export default useUserStore;
